import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalDialogType, RecollectionType } from 'src/app/@core/constants';
import { PosConfirmationService } from 'src/app/@core/services/pos-confirmation.service';
import { PosPrintDocumentsModalService } from 'src/app/@core/services/pos-print-documents-modal.service';
import { Action } from 'src/app/models/actions-model';
import { ColumnTable } from 'src/app/models/column-table';
import { Filter } from 'src/app/models/filter';
import { FilterTable } from 'src/app/models/filter-table';
import { LoansFilter } from 'src/app/models/filters/loans-filter';
import { Loan } from 'src/app/models/loan';
import { Paging } from 'src/app/models/page';
import { LoanService } from 'src/app/services/loan.service';
import { PosTableComponent } from 'src/app/shared/pos-table/pos-table.component';
import { Customer } from './../../../../models/customer';
import { CustomerLoansDetailModalService } from './../../services/customer-loans-detail-modal.service';

@Component({
  selector: 'app-customer-loans-active',
  templateUrl: './customer-loans-active.component.html',
  styleUrls: ['./customer-loans-active.component.sass'],
})
export class CustomerLoansActiveComponent implements OnInit {
  @ViewChild('loansTable') loanTableComponent!: PosTableComponent;

  @Output()
  getAmountOfLoans = new EventEmitter<number>();

  @Output()
  selectLoanChange = new EventEmitter<Loan>();

  @Output()
  filterContractsAction = new EventEmitter<Array<Loan>>();
  @Input() isRecollectionMode = false;
  @Output()
  @Input('customer') set customerChange(customer: Customer | undefined) {
    this.customer = customer;
    if (this.customer?.code !== '') {
      this.getCustomerLoans({
        page: 0,
        pageSize: 10,
        value: { customerCode: this.customer?.code, hasContractNumber: true },
      });
    }
  }


  @Input() recollectionType: RecollectionType = RecollectionType.QUOTAS_PAYMENT;

  public loans: Paging<Array<Loan>> = new Paging([]);
  private customerCode = '';
  private readonly CURRENCY_CODE = 'HNL';
  private readonly YES = 'SI';
  private readonly NO = 'NO';
  private customer: Customer | undefined;
  private readonly notDetailAvailableSalesOrgCode = [`1300`];

  constructor(
    private router: Router,
    private loanService: LoanService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private customerLoansDetailModalService: CustomerLoansDetailModalService,
    private printDocumentsModalService: PosPrintDocumentsModalService,
    private confirmationService: PosConfirmationService
  ) { }

  ngOnInit(): void { }

  async getCustomerLoans(filter: Filter<LoansFilter>): Promise<void> {
    filter.value.from = this.isRecollectionMode ? "CONTRACTRECOLLECTION" : filter.value.from;
    const loansResponse = await this.loanService
      .getActiveLoansByFilters(filter)
      .toPromise();
    this.loans = loansResponse.response;

    this.loans.content.forEach((loan) => {
      const date = this.getDateFromString(
        loan.creationDate || loan.periodStartingDate
      );
      loan.creationDate = this.datePipe.transform(date, 'yyyy-MM-dd') ?? '';
      loan.delayedAmount = loan.delayedAmount || 0;
    });

    this.getAmountOfLoans.emit(this.loans.totalElements);
    this.filterContractsAction.emit(this.loans.content);
  }

  getLoansHeaders(): Array<ColumnTable> {
    let columns = [
      new ColumnTable('salesOrg', 'Código Empresa'),
      new ColumnTable('creationDate', 'Fecha de creación', { type: 'date' }),
      new ColumnTable('contractNumber', 'Numero Contrato'),
      new ColumnTable('totalFinanced', 'Total de Contrato', {
        type: 'currency',
      }),
      new ColumnTable('arrears', 'Valor Vencido', { type: 'currency' }),
      new ColumnTable('paymentDay', 'Día de Pago'),
      new ColumnTable('term', 'Plazo', { secondText: 'Meses' }),
      new ColumnTable('feeAmount', 'Valor Cuota', { type: 'currency' }),
      new ColumnTable('code', 'Opciones', {
        type: 'action',
        actions: this.getActions(),
      }),
    ];

    if (this.isRecollectionMode && (this.recollectionType === RecollectionType.CONDONATION || this.recollectionType === RecollectionType.PAY_OFF)) {
      columns.unshift(
        new ColumnTable('select', 'Sel.', {
          type: 'switch',
          editable: true,
          switchFn: (param) => this.handlePayoff(param),
        })
      );
    }

    if(this.recollectionType === RecollectionType.CONDONATION || this.recollectionType === RecollectionType.PAY_OFF){
      columns = columns.filter(column => column.type !== 'currency');
    }

    return columns;
  }

  getLoansFilters(): Array<FilterTable> {
    let filters = [
      new FilterTable('contractNumber', 'Contrato'),

    ];

    if (!this.isRecollectionMode) {
      filters = [...filters, new FilterTable('startDate', 'Fecha desde', { type: 'date' }),
      new FilterTable('endDate', 'Fecha hasta', { type: 'date' })];
    }
    return filters;
  }

  getLoansData(params: Filter<LoansFilter>): void {
    const filter = params;
    filter.value.customerCode = this.customer?.code;
    filter.value.hasContractNumber = true;

    if (filter.value.startDate) {
      filter.value.startDate = `${filter.value.startDate} 00:00:00 UTC`;
    }

    if (filter.value.endDate) {
      filter.value.endDate = `${filter.value.endDate} 00:00:00 UTC`;
    }
    console.log(filter);
    this.getCustomerLoans(filter);
  }

  private getDateFromString(stringDate: string): Date {
    const year = +stringDate.substring(0, 4);
    const month = +stringDate.substring(4, 6);
    const day = +stringDate.substring(6);
    const date = new Date(year, month - 1, day);

    return date;
  }

  private handleViewLoanDetails(loan: Loan): void {
    this.customerLoansDetailModalService.show(loan);
  }

  private handlePayoff(loan: Loan): void {
    this.selectLoanChange.emit(loan);
  }

  private getActions(): Array<Action> {
    const actions: Array<Action> = [
      {
        fn: (loan: Loan) => this.handleViewLoanDetails(loan),
        icon: 'fa fa-info',
        tooltip: 'Ver detalle',
        isDisabled: false,
        disabledFn: (loan: Loan) =>
          this.notDetailAvailableSalesOrgCode.includes(loan.salesOrg) ||
          loan.invoices.length <= 0,
      },
      {
        fn: async (loan: Loan) => {

          await this.printDocumentsModalService.showPrintLoanDocuments(
            loan,
            this.customer!,
            ""
          );
        },
        icon: 'fa fa-print',
        tooltip: 'Imprimir documentos',
        isDisabled: false,
      },
      {
        fn: async (loan: Loan) => {
          if (await this.confirmationService.showAsync('Atencion!!', '¿Esta seguro de ir a devolucion de contrato?', ModalDialogType.WARNING)) {
            this.router.navigate([`customer/returnContract`, loan.contractNumber]);
          }
        },
        disabledFn: (loan: Loan) =>
          loan.elapsedDays >= 30 || (this.notDetailAvailableSalesOrgCode.includes(loan.salesOrg) && loan.elapsedDays >= 1) || loan.disputeCase != null || loan.contractCanceled != null || (loan.orderCode != undefined && loan.orderCode.length > 0),
        icon: 'fa fa-undo',
        tooltip: 'Devolver contrato',
        isDisabled: false,
      }
    ];

    return actions;
  }

  handleTableForm(form: any) {
    Object.keys(form.controls)
      .filter(
        (k) =>
          !k.startsWith(`select-${this.loanTableComponent.currentIndex}`) &&
          k.includes('select')
      )
      .map((c) => form.get(c))
      .forEach((c) => c.setValue(false));
  }

}
